import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismMessage } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import MarketSmartInsightsCardSmartInsights from 'lumen/components/market/smart-insights/card-smart-insights';
import type { SmartInsightDay } from 'lumen/models';
import {
  PORTFOLIO_SMART_INSIGHTS_SORT_OPTIONS,
  type PortfolioSmartInsightsSortOption,
} from 'lumen/utils/constants/portfolio-smart-insights-constants';

import styles from './column-smart-insights.module.css';

export interface MarketSmartInsightsColumnSmartInsightsSignature {
  Args: {
    groupedInsights: Record<string, SmartInsightDay>;
    hasError?: boolean;
    hasFetchedData: boolean;
    id: string;
    isGroupByStayDate: boolean;
    isLoading: boolean;
    onHotelInsightsCardClick: (smartInsightDay: SmartInsightDay) => void;
    sortBy?: PortfolioSmartInsightsSortOption;
    title: string;
  };
}

export default class MarketSmartInsightsColumnSmartInsights extends Component<MarketSmartInsightsColumnSmartInsightsSignature> {
  sortInsightsByPriority = (
    smartInsightDays: SmartInsightDay[],
  ): SmartInsightDay[] => {
    return smartInsightDays.toSorted((a, b) => {
      return b.stayDateRelevance - a.stayDateRelevance;
    });
  };

  sortInsightsByStayDate = (
    smartInsightDays: SmartInsightDay[],
  ): SmartInsightDay[] => {
    return smartInsightDays.toSorted((a, b) => {
      return dayjs(a.stayDate).isBefore(dayjs(b.stayDate)) ? -1 : 1;
    });
  };

  get hasInsights(): boolean {
    return this.insightEntryCount > 0;
  }

  get insightEntryCount(): number {
    return Object.keys(this.args.groupedInsights).length;
  }

  get isLoading(): boolean {
    return this.args.isLoading && !this.args.hasFetchedData;
  }

  @cached
  get smartInsightDays(): SmartInsightDay[] {
    const insights = Object.values(this.args.groupedInsights);

    if (this.args.isGroupByStayDate) {
      // Stay date view: always sort by priority
      return this.sortInsightsByPriority(insights);
    }

    // Property view: use the selected sort option
    const sortBy =
      this.args.sortBy || PORTFOLIO_SMART_INSIGHTS_SORT_OPTIONS.PRIORITY;

    if (sortBy === PORTFOLIO_SMART_INSIGHTS_SORT_OPTIONS.STAY_DATE) {
      return this.sortInsightsByStayDate(insights);
    }

    return this.sortInsightsByPriority(insights);
  }

  <template>
    <div class={{styles.column}} id={{@id}}>
      <div class="flex items-center gap-200 p-400">
        <h4
          class="overflow-hidden text-ellipsis whitespace-nowrap"
          title={{@title}}
        >
          {{@title}}
        </h4>

        {{#if this.hasInsights}}
          <p class="text-200--regular text-neutral-subdued whitespace-nowrap">
            {{t
              "market.smart_insights.count_insights"
              count=this.insightEntryCount
            }}
          </p>
        {{/if}}
      </div>

      {{#if (and @hasError (not this.isLoading))}}
        <div
          class="flex flex-1 items-center justify-center p-400"
          data-test-id="column-smart-insights-error-state"
        >
          <PrismMessage
            @description={{t "general.error"}}
            @illustration="100x100_critical"
            @title={{t "general.error_title"}}
          />
        </div>
      {{else}}
        <div class="flex flex-col overflow-y-auto px-400 pt-50 pb-400">
          <div class="flex flex-col gap-100">
            {{#each this.smartInsightDays as |smartInsightDay|}}
              <MarketSmartInsightsCardSmartInsights
                @isGroupByStayDate={{@isGroupByStayDate}}
                @smartInsightDay={{smartInsightDay}}
                {{on "click" (fn @onHotelInsightsCardClick smartInsightDay)}}
              />
            {{else}}
              {{#unless this.isLoading}}
                <p
                  class="text-300--regular text-neutral-subdued px-400 py-500 text-center"
                >
                  {{t "market.smart_insights.empty_column.info_text"}}
                </p>
              {{/unless}}
            {{/each}}

            {{#if this.isLoading}}
              <div
                class="{{styles.loading-placeholder}}
                  {{if this.smartInsightDays.length 'mt-400'}}"
              >
                <div class="skeleton-loading-bar"></div>
                <div class="skeleton-loading-bar"></div>
              </div>
            {{/if}}
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
